// LocationForm.tsx - Simplified to match backend API
import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { Loader2, AlertCircle, CheckCircle2, MapPin, Building } from "lucide-react";
import { Location } from "../../../types/location";
import api from "../../../services/axiosInstance";
import ErrorAlert from "../../../shared/components/ui/ErrorAlert";
import Button from "../../../shared/components/ui/button/Button";
import { useLocationDropdowns } from "../../../hooks/useLocationDropdowns";
import { StateDropdown, CityAutocomplete } from "../components";
import { useAuth } from "../../../context/AuthContext";
import { locationService } from "../../../services/locationService";

interface LocationFormProps {
  editingItem: Location | null;
  onAdd: (item: Location) => void;
  onClose: () => void;
}

export default function LocationForm({ 
  editingItem, 
  onAdd, 
  onClose 
}: LocationFormProps) {
  const { user } = useAuth();
  const [apiError, setApiError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Duplicate location checking state
  const [existingLocations, setExistingLocations] = useState<Location[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<{
    show: boolean;
    matchingLocation: Location | null;
  }>({ show: false, matchingLocation: null });
  
  // Location dropdown hook
  const {
    states,
    selectedState,
    selectedCity,
    isLoadingStates,
    error: dropdownError,
    setSelectedState,
    setSelectedCity,
    getStateIso2,
    getStateName
  } = useLocationDropdowns();
  
  const { 
    register, 
    handleSubmit, 
    reset,
    watch,
    formState: { errors, isDirty, isValid },
    setFocus
  } = useForm<any>({
    defaultValues: {
      name: "",
      code: "",
      type: "Project Site",
      address: "",
      city: "",
      state: "",
      country: "India",
      zipCode: "",
      latitude: "",
      longitude: "",
      accessibility: "Good",
      nearestCity: "",
      distanceFromNearestCity: "",
      description: "",
      notes: "",
      status: "active"
    },
    mode: "onChange"
  });

  const watchedName = watch("name");
  const watchedCode = watch("code");
  const watchedDescription = (watch("description") as string) || "";

  // Load existing locations for duplicate checking
  useEffect(() => {
    const loadExistingLocations = async () => {
      try {
        setIsLoadingLocations(true);
        const response = await locationService.getLocations({ 
          limit: 500, 
          page: 1 
        });
        setExistingLocations(response.data || []);
      } catch (error) {
        console.error('Failed to load existing locations:', error);
      } finally {
        setIsLoadingLocations(false);
      }
    };
    loadExistingLocations();
  }, []);

  // Check for duplicate location name
  const checkDuplicateName = useCallback((name: string) => {
    if (!name || name.trim().length < 2) {
      setDuplicateWarning({ show: false, matchingLocation: null });
      return;
    }
    
    const trimmedName = name.trim().toLowerCase();
    
    // Don't check against the current editing item
    const matchingLocation = existingLocations.find(loc => {
      const locId = loc._id || loc.id;
      const editingId = editingItem?._id || editingItem?.id;
      if (editingId && locId === editingId) return false;
      return loc.name.toLowerCase() === trimmedName;
    });
    
    if (matchingLocation) {
      setDuplicateWarning({ show: true, matchingLocation });
    } else {
      setDuplicateWarning({ show: false, matchingLocation: null });
    }
  }, [existingLocations, editingItem]);

  // Check for duplicates when name changes
  useEffect(() => {
    checkDuplicateName(watchedName);
  }, [watchedName, checkDuplicateName]);

  // Handle selecting existing location instead of creating new
  const handleSelectExistingLocation = () => {
    if (duplicateWarning.matchingLocation) {
      onAdd(duplicateWarning.matchingLocation);
      onClose();
    }
  };

  // Set form values when editing
  useEffect(() => {
    if (editingItem) {
      reset({
        name: editingItem.name,
        code: editingItem.code,
        type: editingItem.type,
        address: editingItem.address || "",
        city: editingItem.city || "",
        state: editingItem.state || "",
        country: editingItem.country || "India",
        zipCode: editingItem.zipCode || "",
        latitude: editingItem.latitude || "",
        longitude: editingItem.longitude || "",
        accessibility: editingItem.accessibility || "Good",
        nearestCity: editingItem.nearestCity || "",
        distanceFromNearestCity: editingItem.distanceFromNearestCity || "",
        description: editingItem.description || "",
        notes: editingItem.notes || "",
        status: editingItem.status
      });
      
      // Sync dropdown values
      if (editingItem.state) {
        const stateIso2 = getStateIso2(editingItem.state);
        if (stateIso2) {
          setSelectedState(stateIso2);
        }
      }
      if (editingItem.city) {
        setSelectedCity(editingItem.city);
      }
    } else {
      reset();
      setSelectedState('');
      setSelectedCity('');
    }
    setFocus("name");
  }, [editingItem, reset, setFocus, getStateIso2, setSelectedState, setSelectedCity]);

  const onSubmit = async (data: any, event?: React.BaseSyntheticEvent) => {
    // CRITICAL: Prevent form submission from bubbling to parent form
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    setIsSubmitting(true);
    setApiError(null);
    setShowSuccess(false);
    
    try {
      // Validate user ID
      const userId = user?._id || user?.id;
      if (!userId) {
        setApiError("User information not found. Please log in again.");
        setIsSubmitting(false);
        return;
      }

      let res;
      const payload: any = {
        name: data.name.trim(),
        code: data.code.trim().toUpperCase(),
        type: data.type,
        status: "active"
      };
      
      // Only include optional fields if they have values
      if (data.address?.trim()) {
        payload.address = data.address.trim();
      }
      if (selectedCity?.trim()) {
        payload.city = selectedCity.trim();
      }
      if (selectedState) {
        payload.state = getStateName(selectedState);
      }
      if (data.country?.trim()) {
        payload.country = data.country.trim();
      }
      if (data.zipCode?.trim()) {
        payload.zipCode = data.zipCode.trim();
      }
      if (data.latitude?.trim()) {
        payload.latitude = data.latitude.trim();
      }
      if (data.longitude?.trim()) {
        payload.longitude = data.longitude.trim();
      }
      if (data.accessibility) {
        payload.accessibility = data.accessibility;
      }
      if (data.nearestCity?.trim()) {
        payload.nearestCity = data.nearestCity.trim();
      }
      if (data.distanceFromNearestCity) {
        payload.distanceFromNearestCity = parseFloat(data.distanceFromNearestCity);
      }
      if (data.description?.trim()) {
        payload.description = data.description.trim();
      }
      if (data.notes?.trim()) {
        payload.notes = data.notes.trim();
      }
      
      if (editingItem) {
        // Validate that editingItem has a valid ID
        const locationId = String(editingItem._id || editingItem.id || "");
        if (!locationId) {
          setApiError("Invalid location ID. Please refresh and try again.");
          setIsSubmitting(false);
          return;
        }
        
        // Add updatedBy for edit operations
        payload.updatedBy = userId;
        res = await locationService.updateLocation(locationId, payload);
      } else {
        // For create operations, the backend will handle createdBy automatically
        res = await locationService.createLocation(payload);
      }
      
      setShowSuccess(true);
      setTimeout(() => {
        // res.data is the actual Location object from the normalized ApiResponse
        if (res?.data) {
          onAdd(res.data);
        } else {
          console.error("No location data in response", res);
          // Fallback if data is missing
          onClose();
        }
        onClose();
      }, 1000);
    } catch (err: any) {
      setApiError(err.response?.data?.message || "An error occurred while saving the location");
      console.error("Form submission error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {showSuccess && (
        <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">
              {editingItem ? "Location updated successfully!" : "Location created successfully!"}
            </p>
          </div>
        </div>
      )}
      
      {apiError && (
        <ErrorAlert 
          message={apiError} 
          onClose={() => setApiError(null)} 
          className="mb-4"
        />
      )}
      
      {dropdownError && (
        <ErrorAlert 
          message={`Location data error: ${dropdownError}`} 
          onClose={() => {}} 
          className="mb-4"
        />
      )}
      
      <form 
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit(onSubmit)(e);
        }} 
        className="space-y-8"
      >
        {/* Basic Information Section */}
        <div className="bg-gray-50 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Building className="w-5 h-5" />
            Basic Information
            {isLoadingLocations && (
              <span className="text-sm font-normal text-gray-500 flex items-center gap-1">
                <Loader2 className="w-3 h-3 animate-spin" />
                Checking existing locations...
              </span>
            )}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Name Field */}
            <div className="space-y-2">
              <label 
                htmlFor="name" 
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Location Name *
              </label>
              <div className="relative">
                <input
                  id="name"
                  type="text"
                  {...register("name", { 
                    required: "Location name is required",
                    minLength: {
                      value: 2,
                      message: "Name must be at least 2 characters"
                    },
                    maxLength: {
                      value: 200,
                      message: "Name must be less than 200 characters"
                    },
                  })}
                  className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                    errors.name
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                      : watchedName && !errors.name
                      ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                      : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                  placeholder="Enter location name"
                />
                {watchedName && !errors.name && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
                {errors.name && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              {errors.name && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.name.message as string}
                </p>
              )}
              
              {/* Duplicate Location Warning */}
              {duplicateWarning.show && duplicateWarning.matchingLocation && (
                <div className="mt-2 p-3 bg-amber-50 border border-amber-300 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">
                        Location with this name already exists!
                      </p>
                      <p className="text-sm text-amber-700 mt-1">
                        <strong>"{duplicateWarning.matchingLocation.name}"</strong>
                        {duplicateWarning.matchingLocation.code && ` (${duplicateWarning.matchingLocation.code})`}
                        {duplicateWarning.matchingLocation.city && ` - ${duplicateWarning.matchingLocation.city}`}
                        {duplicateWarning.matchingLocation.state && `, ${duplicateWarning.matchingLocation.state}`}
                      </p>
                      <button
                        type="button"
                        onClick={handleSelectExistingLocation}
                        className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                        Use Existing Location
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Code Field */}
            <div className="space-y-2">
              <label 
                htmlFor="code" 
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Location Code *
              </label>
              <div className="relative">
                <input
                  id="code"
                  type="text"
                  {...register("code", { 
                    required: "Location code is required",
                    minLength: {
                      value: 3,
                      message: "Code must be at least 3 characters"
                    },
                    maxLength: {
                      value: 20,
                      message: "Code must be less than 20 characters"
                    },
                    pattern: {
                      value: /^[A-Z0-9-]+$/i,
                      message: "Code can only contain letters, numbers, and hyphens"
                    }
                  })}
                  className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 uppercase ${
                    errors.code
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                      : watchedCode && !errors.code
                      ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                      : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                  placeholder="Enter location code"
                />
                {watchedCode && !errors.code && (
                  <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
                {errors.code && (
                  <AlertCircle className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-500" />
                )}
              </div>
              {errors.code && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.code.message as string}
                </p>
              )}
            </div>

            {/* Type Field */}
            <div className="space-y-2">
              <label 
                htmlFor="type" 
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Location Type *
              </label>
              <div className="relative">
                <select
                  id="type"
                  {...register("type", { required: "Location type is required" })}
                  className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                    errors.type
                      ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                      : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  }`}
                  defaultValue="Project Site"
                >
                  <option value="Project Site">Project Site</option>
                  <option value="Office">Office</option>
                  <option value="Warehouse">Warehouse</option>
                  <option value="Service Center">Service Center</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              {errors.type && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.type.message as string}
                </p>
              )}
            </div>

            {/* Accessibility Field */}
            <div className="space-y-2">
              <label 
                htmlFor="accessibility" 
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Accessibility
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <div className="relative">
                <select
                  id="accessibility"
                  {...register("accessibility")}
                  className="h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                >
                  <option value="Good">Good</option>
                  <option value="Moderate">Moderate</option>
                  <option value="Difficult">Difficult</option>
                  <option value="Remote">Remote</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Address Information Section */}
        <div className="bg-gray-50 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Address Information
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Address */}
            <div className="md:col-span-2 space-y-2">
              <label 
                htmlFor="address" 
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Address
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <textarea
                id="address"
                rows={3}
                {...register("address", {
                  maxLength: {
                    value: 500,
                    message: "Address must be less than 500 characters"
                  }
                })}
                className={`w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 resize-none ${
                  errors.address
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                }`}
                placeholder="Enter complete address"
              />
              {errors.address && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.address.message as string}
                </p>
              )}
            </div>

            {/* State Dropdown */}
            <StateDropdown
              value={selectedState}
              onChange={setSelectedState}
              states={states}
              isLoading={isLoadingStates}
              error={dropdownError || undefined}
              label="State"
              placeholder="Select State"
            />

            {/* City Autocomplete */}
            <CityAutocomplete
              value={selectedCity}
              onChange={setSelectedCity}
              selectedState={selectedState}
              label="City"
              placeholder="Search or select city"
              disabled={!selectedState || false}
            />

            {/* Country - Fixed as India */}
            <div className="space-y-2">
              <label 
                htmlFor="country" 
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Country
              </label>
              <div className="relative">
                <input
                  id="country"
                  type="text"
                  value="India"
                  readOnly
                  className="h-12 w-full rounded-xl border bg-gray-50 px-4 py-3 text-base text-gray-600 shadow-sm cursor-not-allowed border-gray-200"
                />
                <MapPin className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              </div>
              <p className="text-sm text-gray-500">Country is set to India for all locations</p>
            </div>

            {/* Zip Code */}
            <div className="space-y-2">
              <label 
                htmlFor="zipCode" 
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Zip Code
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="zipCode"
                type="text"
                {...register("zipCode", {
                  maxLength: {
                    value: 20,
                    message: "Zip code must be less than 20 characters"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                  errors.zipCode
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                }`}
                placeholder="Enter zip code"
              />
              {errors.zipCode && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.zipCode.message as string}
                </p>
              )}
            </div>

            {/* Coordinates */}
            <div className="space-y-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Coordinates (Optional)
              </label>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  {...register("latitude")}
                  className="h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  placeholder="Latitude"
                />
                <input
                  type="text"
                  {...register("longitude")}
                  className="h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                  placeholder="Longitude"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Additional Information Section */}
        <div className="bg-gray-50 rounded-xl p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Nearest City */}
            <div className="space-y-2">
              <label 
                htmlFor="nearestCity" 
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Nearest City
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="nearestCity"
                type="text"
                {...register("nearestCity", {
                  maxLength: {
                    value: 100,
                    message: "Nearest city must be less than 100 characters"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                  errors.nearestCity
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                }`}
                placeholder="Enter nearest city"
              />
              {errors.nearestCity && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.nearestCity.message as string}
                </p>
              )}
            </div>

            {/* Distance from Nearest City */}
            <div className="space-y-2">
              <label 
                htmlFor="distanceFromNearestCity" 
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Distance from Nearest City (km)
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                id="distanceFromNearestCity"
                type="number"
                step="0.1"
                min="0"
                {...register("distanceFromNearestCity", {
                  min: {
                    value: 0,
                    message: "Distance must be a positive number"
                  }
                })}
                className={`h-12 w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 ${
                  errors.distanceFromNearestCity
                    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                    : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
                }`}
                placeholder="Enter distance in km"
              />
              {errors.distanceFromNearestCity && (
                <p className="flex items-center gap-1 text-sm text-red-600">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {errors.distanceFromNearestCity.message as string}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Description Field */}
        <div className="space-y-2">
          <label 
            htmlFor="description" 
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Description
            <span className="text-gray-400 font-normal ml-1">(optional)</span>
          </label>
          <div className="relative">
            <textarea
              id="description"
              rows={4}
              {...register("description", {
                maxLength: {
                  value: 1000,
                  message: "Description must be less than 1000 characters"
                }
              })}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 resize-none ${
                errors.description 
                  ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                  : watchedDescription && !errors.description
                  ? "border-green-300 focus:border-green-500 focus:ring-green-500/20"
                  : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
              }`}
              placeholder="Enter a brief description of this location..."
            />
            {watchedDescription && !errors.description && (
              <CheckCircle2 className="absolute right-3 top-4 w-5 h-5 text-green-500" />
            )}
            {errors.description && (
              <AlertCircle className="absolute right-3 top-4 w-5 h-5 text-red-500" />
            )}
          </div>
          <div className="flex items-center justify-between">
            {errors.description && (
              <p className="flex items-center gap-1 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {errors.description.message as string}
              </p>
            )}
            {watchedDescription && !errors.description && (
              <p className="flex items-center gap-1 text-sm text-green-600">
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                Description looks good!
              </p>
            )}
            <span className={`text-xs ${
              (watchedDescription?.length || 0) > 900 ? 'text-red-500' : 
              (watchedDescription?.length || 0) > 800 ? 'text-yellow-500' : 'text-gray-400'
            }`}>
              {watchedDescription?.length || 0}/1000
            </span>
          </div>
        </div>

        {/* Notes Field */}
        <div className="space-y-2">
          <label 
            htmlFor="notes" 
            className="block text-sm font-semibold text-gray-700 mb-2"
          >
            Notes
            <span className="text-gray-400 font-normal ml-1">(optional)</span>
          </label>
          <textarea
            id="notes"
            rows={3}
            {...register("notes", {
              maxLength: {
                value: 2000,
                message: "Notes must be less than 2000 characters"
              }
            })}
            className={`w-full rounded-xl border bg-white px-4 py-3 text-base text-gray-800 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-0 transition-all duration-200 resize-none ${
              errors.notes 
                ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                : "border-gray-300 focus:border-brand-500 focus:ring-brand-500/20"
            }`}
            placeholder="Enter any additional notes..."
          />
          {errors.notes && (
            <p className="flex items-center gap-1 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {errors.notes.message as string}
            </p>
          )}
        </div>
        
        
        {/* Action Buttons - Sticky Footer */}
        <div className="sticky bottom-0 flex flex-col sm:flex-row justify-end gap-3 pt-5 pb-2 border-t border-gray-200 mt-6 bg-white z-10">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={onClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto order-2 sm:order-1"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="sorigin"
            size="lg"
            disabled={isSubmitting || !isDirty || !isValid || duplicateWarning.show}
            className="w-full sm:w-auto min-w-[140px] order-1 sm:order-2"
          >
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {editingItem ? "Updating..." : "Creating..."}
              </div>
            ) : duplicateWarning.show ? (
              "Duplicate Name Found"
            ) : editingItem ? (
              "Update Location"
            ) : (
              "Create Location"
            )}
          </Button>
        </div>
      </form>
    </div>
  );
}